import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { createCaseStep, deleteCaseStep, updateCaseStep } from "@/features/cases/api";
import type { Case, CaseStep } from "@/features/cases/types";

interface CaseStepsDialogProps {
  /** The case whose steps are being managed. Parent mounts this dialog with a
   * `key={caseRecord.id}` so switching cases resets local state without an
   * effect. */
  caseRecord: Case;
  onOpenChange: (open: boolean) => void;
  /** Fires after any change so the list can refetch the open-step count. */
  onChanged: () => void;
}

export function CaseStepsDialog({ caseRecord, onOpenChange, onChanged }: CaseStepsDialogProps) {
  const [steps, setSteps] = useState<CaseStep[]>(caseRecord.steps);
  const [newDescription, setNewDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const caseId = caseRecord.id;

  async function addStep() {
    const description = newDescription.trim();
    if (!description) return;
    setBusy(true);
    try {
      const step = await createCaseStep(caseId, {
        description,
        position: steps.length,
      });
      setSteps((prev) => [...prev, step]);
      setNewDescription("");
      onChanged();
    } catch {
      toast.error("Couldn't add step.");
    } finally {
      setBusy(false);
    }
  }

  async function toggle(step: CaseStep) {
    try {
      const updated = await updateCaseStep(caseId, step.id, {
        is_completed: !step.isCompleted,
      });
      setSteps((prev) => prev.map((s) => (s.id === step.id ? updated : s)));
      onChanged();
    } catch {
      toast.error("Couldn't update step.");
    }
  }

  async function remove(step: CaseStep) {
    try {
      await deleteCaseStep(caseId, step.id);
      setSteps((prev) => prev.filter((s) => s.id !== step.id));
      onChanged();
    } catch {
      toast.error("Couldn't remove step.");
    }
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Workflow Steps</DialogTitle>
        </DialogHeader>

        {steps.length === 0 ? (
          <p className="text-muted-foreground text-sm">No steps yet — add the first below.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {steps.map((step) => (
              <li key={step.id} className="flex items-center gap-3">
                <Checkbox
                  checked={step.isCompleted}
                  aria-label={`Mark "${step.description}" ${step.isCompleted ? "incomplete" : "complete"}`}
                  onCheckedChange={() => toggle(step)}
                />
                <span
                  className={
                    step.isCompleted
                      ? "text-muted-foreground flex-1 text-sm line-through"
                      : "text-foreground flex-1 text-sm"
                  }
                >
                  {step.description}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Delete ${step.description}`}
                  onClick={() => remove(step)}
                >
                  <Trash2 className="text-destructive size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            addStep();
          }}
        >
          <Input
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Add a step…"
            aria-label="New step description"
          />
          <Button type="submit" size="sm" disabled={busy || !newDescription.trim()}>
            <Plus className="size-4" /> Add
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
